import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, useMfaVerify } from '@centrix/api-client';
import type { AuthUser } from '@centrix/api-client';
import {
  Alert,
  Button,
  CollapsibleContainer,
  FormCheckboxField,
  FormInputField,
  Heading,
  Paragraph,
  Spinner,
} from '@centrix/shared-ui';

import { OtpInput } from '@/components/Signup/OtpInput';

const TOTP_LENGTH = 6;

type ChallengeMode = 'totp' | 'backup';

interface MfaChallengeFormProps {
  /** Opaque `mfa_state_token` returned by POST /auth/login/. */
  stateToken: string;
  /** Seconds until the challenge token expires, from the login response. */
  expiresInSeconds: number;
  /** Called with the authenticated user once verification succeeds. */
  onVerified: (user: AuthUser) => void;
  /** Return to the credentials form (e.g. on an expired session). */
  onCancel: () => void;
}

/** Maps the backend's `code` discriminator to a user-facing message. */
function messageForError(err: ApiError): { message: string; expired: boolean } {
  const data = err.data as { code?: string; error?: string } | undefined;
  switch (data?.code) {
    case 'mfa_session_expired':
      return { message: 'Your verification session expired. Please log in again.', expired: true };
    case 'mfa_rate_limited':
      return { message: 'Too many failed attempts. Please try again later.', expired: false };
    case 'mfa_invalid_code':
      return { message: 'Invalid verification code. Please try again.', expired: false };
    default:
      return { message: data?.error || 'Verification failed. Please try again.', expired: false };
  }
}

function formatRemaining(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Second factor of the password login flow. Rendered after POST /auth/login/
 * returns `mfa_required`; submits a TOTP or backup code to /auth/mfa/verify/.
 * SSO sign-in never reaches this component (the IdP owns its own MFA).
 */
export const MfaChallengeForm: FC<MfaChallengeFormProps> = ({
  stateToken,
  expiresInSeconds,
  onVerified,
  onCancel,
}) => {
  const [mode, setMode] = useState<ChallengeMode>('totp');
  const [digits, setDigits] = useState<string[]>(() => Array(TOTP_LENGTH).fill(''));
  const [backupCode, setBackupCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [remaining, setRemaining] = useState(() => Math.max(0, expiresInSeconds));

  const verifyMutation = useMfaVerify();

  // Count the challenge token down to expiry so the user sees how long they
  // have; on zero we lock the form and prompt them to start over.
  useEffect(() => {
    if (remaining <= 0) {
      setExpired(true);
      return;
    }
    const timer = window.setInterval(() => {
      setRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [remaining]);

  const code = mode === 'totp' ? digits.join('') : backupCode.trim();
  const isLoading = verifyMutation.isPending;
  const canSubmit = useMemo(() => {
    if (expired || isLoading) return false;
    return mode === 'totp' ? code.length === TOTP_LENGTH : code.length > 0;
  }, [expired, isLoading, mode, code]);

  const submit = useCallback(
    async (rawCode: string) => {
      if (expired) return;
      setError(null);
      try {
        const result = await verifyMutation.mutateAsync({
          mfa_state_token: stateToken,
          code: rawCode,
          trust_device: trustDevice,
        });
        onVerified(result.user);
      } catch (err) {
        if (err instanceof ApiError) {
          const { message, expired: sessionExpired } = messageForError(err);
          setError(message);
          if (sessionExpired) setExpired(true);
        } else {
          setError('Unable to verify the code. Please try again.');
        }
        // Clear the entered code so the user can retype without stale digits.
        setDigits(Array(TOTP_LENGTH).fill(''));
      }
    },
    [expired, verifyMutation, stateToken, trustDevice, onVerified]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    submit(code);
  };

  const switchMode = (next: ChallengeMode) => {
    setMode(next);
    setError(null);
    setDigits(Array(TOTP_LENGTH).fill(''));
    setBackupCode('');
  };

  return (
    <>
      <Heading as="h2" className="text-[22px] leading-7 font-medium text-black mb-2">
        Two-step verification
      </Heading>
      <Paragraph className="text-sm leading-6 text-gray-body mb-6">
        {mode === 'totp'
          ? 'Enter the 6-digit code from your authenticator app.'
          : 'Enter one of your single-use backup codes.'}
      </Paragraph>

      <CollapsibleContainer isOpen={!!error} className="mb-4">
        {error && (
          <Alert variant="destructive" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CollapsibleContainer>

      <form onSubmit={handleSubmit} className="space-y-5">
        {mode === 'totp' ? (
          <OtpInput
            value={digits}
            onChange={setDigits}
            onComplete={submit}
            length={TOTP_LENGTH}
            disabled={isLoading || expired}
            hasError={!!error}
          />
        ) : (
          <FormInputField
            id="backup-code"
            label="Backup code"
            name="backup-code"
            value={backupCode}
            onChange={(e) => {
              setBackupCode(e.target.value);
              setError(null);
            }}
            placeholder="XXXXXXXX-XXXXXXXX"
            disabled={isLoading || expired}
            autoComplete="one-time-code"
            inputClassName="h-11 px-4 text-sm text-black placeholder:text-gray-placeholder focus-visible:ring-offset-0 border-gray-placeholder"
          />
        )}

        <FormCheckboxField
          id="trust-device"
          label="Trust this device for 30 days"
          checked={trustDevice}
          onCheckedChange={(checked) => setTrustDevice(checked === true)}
          disabled={isLoading || expired}
        />

        <Button
          type="submit"
          variant="auth"
          size="auth"
          disabled={!canSubmit}
          className="w-full leading-6"
        >
          {isLoading ? (
            <>
              <Spinner className="text-white" />
              <span>Verifying...</span>
            </>
          ) : (
            'Verify'
          )}
        </Button>
      </form>

      <div className="mt-4 flex items-center justify-between text-sm leading-6">
        <Button
          type="button"
          variant="tableLink"
          onClick={() => switchMode(mode === 'totp' ? 'backup' : 'totp')}
          disabled={isLoading}
        >
          {mode === 'totp' ? 'Use a backup code instead' : 'Use authenticator app instead'}
        </Button>
        {!expired && (
          <span className="text-gray-placeholder">Expires in {formatRemaining(remaining)}</span>
        )}
      </div>

      <Paragraph className="mt-4.5 text-center text-sm leading-6 text-black">
        <Button type="button" variant="tableLink" onClick={onCancel}>
          Back to login
        </Button>
      </Paragraph>
    </>
  );
};
